<template>
  <div class="design-pipeline">
    <front-page-design-nav theme="light" />

    <section class="hero">
      <img :src="logo" alt="BOX21" class="hero__logo" />
      <h1>{{ hero.line1 }}</h1>
      <p class="hero__sub">{{ hero.line2 }} · {{ hero.line3 }}</p>
    </section>

    <section class="pipeline">
      <div class="pipeline__track">
        <div v-for="(step, i) in steps" :key="step.id" class="pipeline__step">
          <div class="pipeline__node">
            <img :src="step.icon" :alt="step.title" />
            <span class="pipeline__num">{{ i + 1 }}</span>
          </div>
          <h3>{{ step.title }}</h3>
          <p>{{ step.description }}</p>
          <div v-if="i < steps.length - 1" class="pipeline__connector" />
        </div>
      </div>
    </section>

    <section class="examples">
      <article v-for="item in showcase" :key="item.id" class="example-card">
        <img :src="item.image" :alt="item.title" />
        <div>
          <h3>{{ item.title }}</h3>
          <p>{{ item.text }}</p>
        </div>
      </article>
    </section>

    <section class="cta-band">
      <h2>From camera trap to deployed model</h2>
      <button type="button" @click="goToRegister">Start your pipeline</button>
    </section>

    <front-page-design-footer theme="light" />
  </div>
</template>

<script>
import FrontPageDesignNav from '@/components/front_page/designs/FrontPageDesignNav.vue'
import FrontPageDesignFooter from '@/components/front_page/designs/FrontPageDesignFooter.vue'
import { BRAND, HERO, PILLARS, SHOWCASE } from '@/components/front_page/designs/shared'

export default {
  name: 'DesignPipeline',
  components: { FrontPageDesignNav, FrontPageDesignFooter },
  data() {
    return {
      logo: BRAND.logo,
      hero: HERO,
      showcase: SHOWCASE,
      steps: PILLARS.map((p) => ({
        id: p.id,
        icon: p.icon,
        title: p.title,
        description: p.tags.join(', '),
      })),
    }
  },
  methods: {
    goToRegister() { this.$router.push({ path: '/login', query: { tab: 'register' } }) },
  },
}
</script>

<style scoped>
.design-pipeline {
  min-height: 100vh;
  background: #fff;
  font-family: system-ui, sans-serif;
  color: #333;
}

.hero {
  text-align: center;
  padding: 32px 24px 48px;
  background: linear-gradient(180deg, #eef5fa, #fff);
}

.hero__logo {
  height: 44px;
  margin-bottom: 24px;
}

.hero h1 {
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: 800;
  color: #0677b9;
  margin: 0 0 12px;
}

.hero__sub {
  font-size: 1.15rem;
  color: #666;
  margin: 0;
}

.pipeline {
  padding: 48px 24px 80px;
  overflow-x: auto;
}

.pipeline__track {
  display: flex;
  justify-content: center;
  gap: 0;
  min-width: min-content;
  max-width: 1100px;
  margin: 0 auto;
  position: relative;
}

.pipeline__step {
  flex: 1;
  min-width: 200px;
  max-width: 240px;
  text-align: center;
  position: relative;
  padding: 0 16px;
}

.pipeline__node {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: #fff;
  border: 3px solid #0677b9;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 4px 16px rgba(6, 119, 185, 0.2);
}

.pipeline__node img {
  width: 36px;
  height: 36px;
}

.pipeline__num {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: #7bacd6;
  color: #fff;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pipeline__step h3 {
  font-size: 0.95rem;
  margin: 0 0 8px;
  line-height: 1.3;
}

.pipeline__step p {
  font-size: 0.8rem;
  color: #888;
  margin: 0;
}

.pipeline__connector {
  position: absolute;
  top: 40px;
  left: calc(50% + 48px);
  width: calc(100% - 48px);
  height: 3px;
  background: linear-gradient(90deg, #0677b9, #7bacd6);
  z-index: -1;
}

.examples {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px 64px;
}

.example-card {
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  overflow: hidden;
}

.example-card img {
  width: 100%;
  aspect-ratio: 16/10;
  object-fit: cover;
}

.example-card div {
  padding: 20px;
}

.example-card h3 {
  font-size: 1rem;
  margin: 0 0 8px;
  color: #0677b9;
}

.example-card p {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.cta-band {
  background: #0677b9;
  color: #fff;
  text-align: center;
  padding: 64px 24px;
}

.cta-band h2 {
  font-size: 1.5rem;
  margin: 0 0 24px;
}

.cta-band button {
  background: #fff;
  color: #0677b9;
  border: none;
  padding: 14px 32px;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

@media (max-width: 768px) {
  .pipeline__track {
    flex-direction: column;
    align-items: center;
  }
  .pipeline__connector {
    display: none;
  }
}
</style>
